import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Button, AgGridTable } from 'components/common';
import PropTypes from 'prop-types';
import { FaRocket, FaTimes, FaCheckCircle, FaChevronDown, FaChevronUp, FaListAlt } from 'react-icons/fa';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { getCsdReleaseEmailConfig } from 'redux/crm/releaseForecastSlice';
import ForecastEmailFields from '../ForecastEmailFields';
import { recipientGreeting } from '../components/helpers';

const currentPeriodStr = () => dayjs().format('MMM-YYYY');

// Text-only, whitespace-insensitive compare — TinyMCE re-serialises whatever we
// hand it (tags reordered, entities rewritten), so only the words are reliable.
const squash = (html) => String(html || '').replace(/<[^>]*>/g, '').replace(/\s+|&nbsp;/g, '').toLowerCase();

const buildDefaultBody = ({ recipient, period, url }) =>
  `<p>Dear <strong>${recipient}</strong>,</p><p>The 3-month release forecast for <strong>${period}</strong> is ready for review and production planning.</p><p>Please click the link below to access the complete forecast: <a href="${url}" target="_blank">View Forecast</a></p><p>Best Regards</p>`;

const percentCellStyle = (params) => {
  const v = params.value;
  const base = { fontWeight: 600, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  if (v == null) return { ...base, color: '#94a3b8' };
  if (v > 70)   return { ...base, color: '#15803d' };
  if (v > 40)   return { ...base, color: '#92400e' };
  return { ...base, color: '#991b1b' };
};

const PlanningReleaseConfirmDialog = ({ target, onConfirm, onCancel }) => {
  const dispatch = useDispatch();
  const records       = useMemo(() => target?.records || [], [target?.records]);
  const defaultPeriod = target?.period;

  const [confirmPeriod] = useState(defaultPeriod || currentPeriodStr());
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [subject, setSubject] = useState(`3 MONTHS RELEASE FORECAST ${confirmPeriod}`);
  const [body, setBody] = useState('');
  const [bodyEdited, setBodyEdited] = useState(false);
  const generatedBody = useRef('');
  const [showReleaseDetails, setShowReleaseDetails] = useState(false);

  // Greets every recipient, e.g. "Dear MIH/NGD".
  const recipient = useMemo(() => recipientGreeting(toEmails, 'Planning Team'), [toEmails]);

  // Deep-links to the Planning grid for the period being released, the same way
  // the Sales dialog links to the CSD grid.
  const forecastUrl = useMemo(
    () => `${window.location.origin}/crm/release-forecast/planning${confirmPeriod ? `?period=${encodeURIComponent(confirmPeriod)}` : ''}`,
    [confirmPeriod]
  );

  useEffect(() => {
    if (bodyEdited) return;
    const generated = buildDefaultBody({ recipient, period: confirmPeriod, url: forecastUrl });
    generatedBody.current = generated;
    setBody(generated);
  }, [recipient, confirmPeriod, forecastUrl, bodyEdited]);

  const handleBodyChange = (value) => {
    if (squash(value) !== squash(generatedBody.current)) setBodyEdited(true);
    setBody(value);
  };

  useEffect(() => {
    dispatch(getCsdReleaseEmailConfig()).unwrap().then((config) => {
      setToEmails(config.to_emails || []);
      setCcEmails(config.cc_emails || []);
    }).catch(() => {});
  }, [dispatch]);

  const uniqueProjects = useMemo(() => records.reduce((acc, r) => {
    const csdId = r.csd_id || r.id;   // UUID of the CSD record — sent to backend
    const projectId = r.project?.id;   // project id — display only
    if (!acc.find((p) => p.csdId === csdId)) {
      acc.push({
        csdId,
        id:        projectId,
        name:      r.project?.name || r.project_name || '—',
        jobNo:     r.project?.j_number || r.project?.q_number || '—',
        percent:   r.percent_change_of_release ?? r.percent_change,
        salesUnit: r.sales_unit?.name || r.sales_unit_name || '—',
      });
    }
    return acc;
  }, []), [records]);

  const columnDefs = useMemo(() => [
    {
      headerName: 'Job No.',
      field: 'jobNo',
      width: 120,
      cellStyle: { color: '#1d4ed8', fontWeight: 700, fontFamily: 'monospace', display: 'flex', alignItems: 'center' },
    },
    {
      headerName: 'Project Name',
      field: 'name',
      width: 200,
      cellStyle: { color: '#374151', display: 'flex', alignItems: 'center' },
      tooltipField: 'name',
    },
    {
      headerName: '% Chance of Release',
      field: 'percent',
      width: 90,
      cellStyle: percentCellStyle,
      valueFormatter: (params) => (params.value != null ? `${params.value}%` : '—'),
      headerClass: 'ag-center-header',
    },
    {
      headerName: 'Sales unit',
      field: 'salesUnit',
      width: 90,
      cellStyle: { color: '#6b7280', textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' },
      headerClass: 'ag-right-header',
    },
  ], []);

  const gridHeight = useMemo(() => {
    const rowH    = 35;  // matches AgGridTable default rowHeight
    const headerH = 100;  // matches AgGridTable default headerHeight
    const rows    = Math.min(Math.max(uniqueProjects.length, 1), 6);
    return `${rows * rowH + headerH}px`;
  }, [uniqueProjects.length]);

  return (
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4 tw-bg-black tw-bg-opacity-50"
      style={{ zIndex: 999999 }}
    >
      <div className="tw-bg-white tw-rounded-lg tw-shadow-2xl tw-w-full tw-max-w-3xl tw-overflow-hidden tw-max-h-[92vh] tw-flex tw-flex-col">
        {/* Header */}
        <div className="tw-bg-gradient-to-r tw-from-primary-600 tw-to-blue-600 tw-px-6 tw-py-4 tw-flex tw-items-center tw-gap-3">
          <FaRocket className="tw-text-white" size={18} />
          <div>
            <h5 className="tw-font-bold tw-text-base tw-text-white tw-mb-0">Release to Planning</h5>
            <p className="tw-text-primary-100 tw-text-xs tw-mt-0.5">Review and confirm records before releasing</p>
          </div>
        </div>

        <div className="tw-p-6 tw-overflow-y-auto">
          {/* Warning */}
          <p className="tw-text-xs tw-text-amber-700 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded tw-px-3 tw-py-2">
            ⚠️ Backlogs will not be allowed to be released and will be removed. Once released to Planning, records will be <strong>locked permanently</strong> and cannot be edited.
          </p>

          <ForecastEmailFields
            toEmails={toEmails} ccEmails={ccEmails} subject={subject} body={body}
            onToChange={setToEmails} onCcChange={setCcEmails}
            onSubjectChange={setSubject} onBodyChange={handleBodyChange}
          />

          {/* Secondary release details: collapsed by default to keep email prominent. */}
          <div className="tw-mt-4 tw-rounded tw-border tw-border-slate-200 tw-overflow-hidden tw-bg-white">
            <button
              type="button"
              onClick={() => setShowReleaseDetails((visible) => !visible)}
              aria-expanded={showReleaseDetails}
              className="tw-w-full tw-flex tw-items-center tw-gap-3 tw-border-0 tw-bg-slate-50 hover:tw-bg-slate-100 tw-px-4 tw-py-3 tw-text-left tw-cursor-pointer tw-transition-colors"
            >
              <span className="tw-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded tw-bg-blue-100 tw-text-blue-600 tw-shrink-0">
                <FaListAlt size={13} />
              </span>
              <span className="tw-flex-1">
                <span className="tw-block tw-text-xs tw-font-bold tw-text-slate-800">Release details</span>
                <span className="tw-block tw-text-[11px] tw-text-slate-500 tw-mt-0.5">
                  {confirmPeriod} · {uniqueProjects.length} project{uniqueProjects.length !== 1 ? 's' : ''} selected
                </span>
              </span>
              <span className="tw-text-slate-400">
                {showReleaseDetails ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
              </span>
            </button>

            {showReleaseDetails && (
              <div className="tw-border-t tw-border-slate-200 tw-p-3">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                  <FaCheckCircle className="tw-text-emerald-500" size={13} />
                  <span className="tw-text-xs tw-font-semibold tw-text-gray-700">
                    {uniqueProjects.length} project{uniqueProjects.length !== 1 ? 's' : ''} will be released
                  </span>
                </div>
                <div className="tw-rounded tw-overflow-hidden tw-border tw-border-gray-200">
                  <AgGridTable
                    columnDefs={columnDefs}
                    rowData={uniqueProjects}
                    height={gridHeight}
                    rowNumbers={true}
                    sort={true}
                    filter={false}
                    pagination={true}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="tw-flex tw-gap-2 tw-justify-end tw-mt-5">
            <Button variant="secondary" onClick={onCancel}>
              <FaTimes className="tw-mr-1.5 tw-inline" size={11} /> Cancel
            </Button>
            <Button
              onClick={() => onConfirm(confirmPeriod, uniqueProjects.map((p) => p.csdId).filter(Boolean), { to_emails: toEmails, cc_emails: ccEmails, subject, body })}
              disabled={!confirmPeriod || uniqueProjects.length === 0 || toEmails.length === 0}
              style={{ backgroundColor: '#16a34a', color: '#fff' }}
            >
              <FaRocket size={11} className="tw-mr-1.5 tw-inline" />
              Confirm &amp; Release {uniqueProjects.length > 0 && `(${uniqueProjects.length})`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

PlanningReleaseConfirmDialog.propTypes = {
  target:    PropTypes.shape({ records: PropTypes.array, period: PropTypes.string }).isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel:  PropTypes.func.isRequired,
};

export default PlanningReleaseConfirmDialog;
